/* eslint no-new: 0 */
import '../styles/frontend-styles.scss';

import Vue from 'vue';

import { extractCountryCode } from '~/lib/extractCountryCode';
import mitt from 'mitt';
import VueApollo from 'vue-apollo';
import VueContentPlaceholders from 'vue-content-placeholders';
import apolloClient from '~/apolloClient';
import Filters from '~/filters';
import store from '~/store';
import i18n, { loadTranslations } from '~/i18n';
import router from '~/router';

import handleNavbar from '~/lib/stickyNavbar';
import handleFooter from '~/lib/handleFooter';
import removeUTMandGCLID from '~/lib/removeUtmGclid';
import initializeSentry, { reportError, reportWarning } from '~/lib/sentry';
import { globalMixin } from '~/lib/mixins';

import posthogPlugin from '../plugins/posthog';

Vue.config.performance = process.env.NODE_ENV !== 'production';

require('~/ui-library');

Vue.prototype.$emitter = mitt();

// Add global error reporting
Vue.prototype.$reportError = reportError;
Vue.prototype.$reportWarning = reportWarning;

initializeSentry(Vue);
removeUTMandGCLID();
loadTranslations();
handleNavbar();

const apolloProvider = new VueApollo({
  defaultClient: apolloClient,
});

Vue.mixin(globalMixin);

Vue.use(VueApollo);
Vue.use(VueContentPlaceholders);
Vue.use(Filters);
Vue.use(posthogPlugin);

store.dispatch('auth/setAuthFromWindow');
store.dispatch('config/getConfigData');
store.dispatch('env/setLangAndCountry', extractCountryCode(document.documentElement.lang));

document.addEventListener('DOMContentLoaded', () => {
  const appElement = document.getElementById('app');

  if (appElement) {
    const props = (() => {
      try {
        const dataset = appElement.dataset.vue;
        return dataset ? JSON.parse(dataset) : {};
      } catch {
        return {};
      }
    })();

    import('~/modules/layout/Layout.vue').then((module) => {
      new Vue({
        el: '#app',
        name: 'MainApp',
        store,
        router,
        i18n,
        apolloProvider,
        provide() {
          return props;
        },
        render: h => h(module.default),
      });
    });

    document.body.classList.add('vue-page');
  } else if (document.getElementById('flashes-data')) {
    import('~/modules/FlashMessages.vue').then((module) => {
      new Vue({
        el: '#flashes',
        name: 'FlashMessages',
        store,
        router,
        i18n,
        render: h => h(module.default),
      });
    });
  }

  if (document.getElementById('vue-navbar')) {
    import('~/modules/layout/navbar/Navbar.vue').then((module) => {
      new Vue({
        el: '#vue-navbar',
        name: 'Navigation',
        store,
        router,
        i18n,
        apolloProvider,
        watch: {
          $route: {
            handler(to, _from) {
              handleFooter(this, to);
            },
            immediate: true,
          },
        },
        render: h => h(module.default),
      });
    });
  }
});

if (document.getElementById('hp_blogposts')) {
  import(/* webpackChunkName: "homepage" */ '~/components/HpBlogposts.vue').then((module) => {
    new Vue({
      el: '#hp_blogposts',
      name: 'HPBlogPosts',
      store,
      i18n,
      apolloProvider,
      render: h => h(module.default, {
        props: {
          onHp: true,
        },
      }),
    });
  });
}

if (document.getElementById('vue-saved-lines-migration')) {
  import(/* webpackChunkName: "form" */ '~/pages/LinesMigrator.vue').then((module) => {
    new Vue({
      el: '#vue-saved-lines-migration',
      name: 'SavedLinesMigrator',
      store,
      router,
      i18n,
      render: h => h(module.default),
    });
  });
}

document.addEventListener('DOMContentLoaded', async () => {
  if (document.getElementById('vue-billapp')) {
    const BillAppModule = await import('~/billapp/App.vue');
    const migrationModule = await import('~/billapp/store/migration');

    // Make sure to get the default export if the modules are using ES modules.
    const BillApp = BillAppModule.default;
    const migration = migrationModule.default;

    store.registerModule('migration', migration);

    new Vue({
      el: '#vue-billapp',
      name: 'Billapp',
      store,
      render: h => h(BillApp),
    });
  }

  if (document.getElementById('vue-billapp-auto-import')) {
    const BillappAutoImportModule = await import('~/billapp-auto-import/App.vue');
    const billappAutoImportModule = await import('~/billapp-auto-import/store/index');

    // Make sure to get the default export if the modules are using ES modules.
    const BillappAutoImport = BillappAutoImportModule.default;
    const billappAutoImport = billappAutoImportModule.default;

    store.registerModule('billappAutoImport', billappAutoImport);

    new Vue({
      el: '#vue-billapp-auto-import',
      name: 'BillappAutoImport',
      store,
      i18n,
      router,
      render: h => h(BillappAutoImport),
    });
  }
});
